import { memo, useCallback, useMemo } from "react";
import { Button, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import TableCellComponent from "components/table/TableCellComponent";
import { DataHeaderType } from "components/table/TableRowComponent";
import { HEADER_WHEEL_LIST } from "constants/data";
import EditableNumberCell from "components/table/EditableNumberCell";
import { getUserType } from "util/auth";
import useWheelBrandHandlers from "../hooks/useWheelBrandHandlers";
import { WinterWheelPriceItem } from "../types/winter-wheel-price";

const buttonStyles = {
  border: "1px solid var(--gray-250)",
  backgroundColor: "var(--white)",
  color: "var(--gray-600)",
  fontSize: { xs: "14px", xl: "15px" },
  borderRadius: "10px",
  minWidth: { xs: 50, lg: 80 },
  maxHeight: 32,
  whiteSpace: "pre",
  boxShadow: "var(--shadow)",
} as const;

interface WinterWheelPriceSettingTableProps {
  winterWheelPriceList: WinterWheelPriceItem[];
  openModal: (item?: WinterWheelPriceItem) => void;
  onUpdate: (id: number, key: keyof WinterWheelPriceItem, data: number | string) => void;
}

const WINTER_WHEEL_PRICE_HEADER: DataHeaderType[] = [
  {
    label: "インチ",
    sx: { width: "174px", textAlign: "center" },
  },
  {
    label: "リム幅",
    sx: { width: "174px", textAlign: "center" },
  },
  {
    label: "PCD",
    sx: { width: "174px", textAlign: "center" },
  },
  {
    label: "売価（１本・税込）",
    sx: { width: "174px", textAlign: "center" },
  },
];

/**
 * @param {WinterWheelPriceSettingTableProps} props - The component props
 */
const WinterWheelPriceSettingTable = memo<WinterWheelPriceSettingTableProps>(
  ({ winterWheelPriceList, openModal, onUpdate }) => {
    // Create a wrapper function to match the generic hook signature
    const handleUpdate = useCallback(
      (identifier: string | number, key: keyof WinterWheelPriceItem, data: string | number) => {
        // Since our onUpdate only accepts number id, we need to ensure identifier is number
        if (typeof identifier === "number") {
          onUpdate(identifier, key, data);
        }
      },
      [onUpdate],
    );

    const {
      handleNumberBlur,
      handleNumberInput,
      handleNumberKeyDown,
      handleFocus,
      getError,
      parseNumber,
    } = useWheelBrandHandlers<WinterWheelPriceItem>({ onUpdate: handleUpdate });

    const tableContainerStyles = useMemo(
      () => ({
        boxShadow: "none",
        minWidth: 800,
        with: "100%",
        borderCollapse: "unset" as const,
        borderSpacing: "2px 1px",
      }),
      [],
    );

    // Memoize header cell styles
    const brandCdCellStyles = useMemo(
      () => ({
        backgroundColor: "var(--offwhite-400)",
        border: "2px solid var(--silver-150)",
      }),
      [],
    );

    return (
      <TableContainer component={Table} sx={tableContainerStyles}>
        <TableHead>
          <TableRow>
            {WINTER_WHEEL_PRICE_HEADER.map((header) => (
              <TableCellComponent
                type="header"
                key={header.label as string}
                sx={{ whiteSpace: "pre-line", ...header.sx }}
              >
                {header.label}
              </TableCellComponent>
            ))}
            <TableCellComponent sx={{ px: 0, width: 100, borderBottom: "none" }}>
              <Button variant="outlined" sx={buttonStyles} onClick={() => openModal()}>
                全件確認
              </Button>
            </TableCellComponent>
          </TableRow>
        </TableHead>
        <TableBody>
          {winterWheelPriceList.map((item, index) => {
            const userType = getUserType();
            const isStoreOrCompany = userType === "C0002" || userType === "C0006";
            const cellColor =
              isStoreOrCompany && item.mst_winter_wheel_selling_price_honbu_flag === 0
                ? "var(--red-800)"
                : "var(--black)";
            return (
            <TableRow key={item.id}>
              {/* Brand Code Cell - Read Only */}
              <TableCellComponent sx={brandCdCellStyles}>{item.wheel_width}</TableCellComponent>
              <TableCellComponent sx={brandCdCellStyles}>{item.wheel_rim_diam}</TableCellComponent>
              <TableCellComponent sx={brandCdCellStyles}>{item.wheel_pcd}</TableCellComponent>

              {/* Sale Price Rate Cost Cell - Editable Number */}
              <EditableNumberCell
                value={
                  item.wheel_selling_price !== undefined && item.wheel_selling_price !== null
                    ? String(item.wheel_selling_price)
                    : ""
                }
                field="wheel_selling_price"
                identifier={item.id}
                index={index}
                maxWidth={HEADER_WHEEL_LIST[4].maxWidth || "130px"}
                color={cellColor}
                hasError={getError("wheel_selling_price", index)}
                onBlur={handleNumberBlur}
                onInput={handleNumberInput}
                onKeyDown={handleNumberKeyDown}
                onFocus={handleFocus}
                parseNumber={parseNumber}
              />

              {/* Action Buttons Cell */}
              <TableCellComponent sx={{ px: 0, borderBottom: "none" }}>
                <Button variant="outlined" sx={buttonStyles} onClick={() => openModal(item)}>
                  確認
                </Button>
              </TableCellComponent>
            </TableRow>
          ); })}
        </TableBody>
      </TableContainer>
    );
  },
);

WinterWheelPriceSettingTable.displayName = "WinterWheelPriceSettingTable";

export default WinterWheelPriceSettingTable;
